import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronLeft } from 'lucide-react';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { BookGoalScreen } from './BookGoalScreen';
import { SummaryScreen } from './SummaryScreen';
import { ProgressScreen } from './ProgressScreen';
import { launchConfetti } from './confetti';
import { ENCOURAGEMENTS, STEPPER_BOUNDS } from '@/constants/reading-tracker';
import { booksService } from '@/services/books.service';
import { bookmarksService } from '@/services/bookmarks.service';
import { ApiError } from '@/services/http';
import { readingTrackerService } from '@/services/reading-tracker.service';
import { useSessionStore } from '@/store/session.store';
import {
  EMPTY_DRAFT,
  useReadingTrackerStore,
  type WizardScreen,
} from '@/store/reading-tracker.store';
import type { BookAccountMetadataView, BookView } from '@/types/api';

/**
 * Embeddable reading-tracker UI. Lives inside the BookDetailModal slideout
 * (and could be reused in any other host) — the standalone route page at
 * `/reader/:readerId/book/:bookId/tracker` remains for backward
 * compatibility with deep links.
 *
 * Behaviour mirrors `ReadingTrackerPage` minus the in-wizard book-recap
 * screen (we're already inside the book detail context, so a second
 * "this is the book you tapped" card is redundant). The Zustand store
 * `useReadingTrackerStore` is shared with the route page so:
 *   - The active goal is cached once per (reader, book) pair.
 *   - Drafts (goalType / perDay) survive a refresh.
 *   - The modal's button label stays in sync with mutations from inside
 *     the panel (create / abandon).
 *
 * Any persisted `'book'` screen from the route-page flow is normalised to
 * `'bookGoal'` here so a returning user lands on a screen the panel
 * actually renders.
 */
export interface BookTrackerPanelProps {
  book: BookView;
  readerId: string;
  /**
   * Fires when the user taps "Back to book" on any screen — the host is
   * responsible for restoring its prior view (e.g. swapping the modal
   * back to the Details tab).
   */
  onBackToBook: () => void;
}

export function BookTrackerPanel({ book, readerId, onBackToBook }: BookTrackerPanelProps) {
  const accessToken = useSessionStore((s) => s.accessToken);

  const keyFor = useReadingTrackerStore((s) => s.keyFor);
  const setScreen = useReadingTrackerStore((s) => s.setScreen);
  const setDraft = useReadingTrackerStore((s) => s.setDraft);
  const setGoal = useReadingTrackerStore((s) => s.setGoal);
  const resetKey = useReadingTrackerStore((s) => s.resetKey);

  const key = keyFor(readerId, book.id);
  const screenFromStore = useReadingTrackerStore((s) => s.currentScreens[key]);
  const draftFromStore = useReadingTrackerStore((s) => s.drafts[key]);
  const goalFromStore = useReadingTrackerStore((s) => s.goalsByBook[key]);

  const draft = draftFromStore ?? EMPTY_DRAFT;

  // Canonical book payload. The `book` prop typically arrives from a list
  // endpoint (Library/Catalog/ReadingList → BookDetailModal), and as of
  // 2026-05 those list RPCs (`get_books_with_filters`,
  // `embedding_search_books`, etc.) don't project `pages` / `chapters` —
  // only `get_book(uuid)` does. Without a refetch the picker thinks the
  // book has neither and surfaces the empty-state. Refetching via
  // `getById()` guarantees the panel always works against a complete
  // BookView regardless of how the modal was opened.
  const [fullBook, setFullBook] = useState<BookView | null>(null);
  const renderBook = fullBook ?? book;

  // Per-account effective page/chapter counts (override ?? catalogue). Drives
  // the picker availability, the stepper max, and the plan-summary math.
  const [accountMeta, setAccountMeta] = useState<BookAccountMetadataView | null>(null);

  const [bootstrapping, setBootstrapping] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ----- Bootstrap: probe active goal + refetch full book in parallel ----
  // Goal cache: if the modal already wrote it via its own probe, fast-
  // path. Book refetch: always, because the prop is potentially a partial
  // shape from a list endpoint.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setBootstrapping(true);

    const goalPromise = (async () => {
      if (goalFromStore !== undefined) return goalFromStore;
      try {
        const active = await readingTrackerService.active(readerId, book.id);
        if (!cancelled) setGoal(key, active);
        return active;
      } catch (err) {
        // Soft-fail: assume no active goal so the user lands on the
        // wizard. Real issues will resurface on create.
        console.warn('[reading-tracker] active probe failed:', err);
        if (!cancelled) setGoal(key, null);
        return null;
      }
    })();

    const bookPromise = (async () => {
      try {
        return await booksService.getById(book.id);
      } catch (err) {
        // If the refetch fails we fall back to the prop book — the user
        // may see the "no page/chapter info" empty-state if the prop
        // arrived without those columns, but at least the panel doesn't
        // get stuck on a spinner.
        console.warn('[reading-tracker] book refetch failed:', err);
        return null;
      }
    })();

    // Per-account effective counts. Soft-fail to null — the picker falls back
    // to the catalogue counts off `renderBook` so the wizard still works.
    const metaPromise = (async () => {
      try {
        return await booksService.getAccountMetadata(book.id);
      } catch (err) {
        console.warn('[reading-tracker] account-metadata fetch failed:', err);
        return null;
      }
    })();

    (async () => {
      const [goal, refreshed, meta] = await Promise.all([
        goalPromise,
        bookPromise,
        metaPromise,
      ]);
      if (cancelled) return;
      if (refreshed) setFullBook(refreshed);
      if (meta) setAccountMeta(meta);
      reconcileInitialScreen(goal, screenFromStore);
      setBootstrapping(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, accessToken]);

  function reconcileInitialScreen(
    active: typeof goalFromStore,
    persisted: WizardScreen | undefined,
  ) {
    if (active) {
      setScreen(key, 'progress');
      return;
    }
    // Normalise the legacy `'book'` recap screen (used only by the route
    // page) to `'bookGoal'` so the panel never tries to render a screen
    // it doesn't know about.
    if (persisted && persisted !== 'book') return;
    setScreen(key, 'bookGoal');
  }

  // Resolve the screen we should render. The fallback to 'bookGoal' covers
  // the brief tick before the bootstrap effect writes its first value.
  const screen: WizardScreen =
    screenFromStore && screenFromStore !== 'book' ? screenFromStore : 'bookGoal';

  // ----- Action handlers --------------------------------------------------
  const goTo = (next: WizardScreen) => setScreen(key, next);

  const handleGoalTypeChange = (type: typeof draft.goalType) => {
    if (!type) return;
    setDraft(key, { goalType: type, perDay: STEPPER_BOUNDS[type].default });
  };

  const handlePerDayChange = (perDay: number) => {
    setDraft(key, { perDay });
  };

  const handleSaveCounts = async (patch: {
    pages?: number | null;
    chapters?: number | null;
  }) => {
    const updated = await booksService.saveAccountMetadata(book.id, patch);
    setAccountMeta(updated);
  };

  const handleConfirmPlan = async () => {
    if (!draft.goalType) return;
    setSubmitting(true);
    try {
      const created = await readingTrackerService.create({
        readerId,
        bookId: book.id,
        goalType: draft.goalType,
        perDay: draft.perDay,
      });
      setGoal(key, created);
      // Auto-bookmark the book so it surfaces on the reader's Bookshelf
      // for the duration of the plan. `bookmarksService.create` is
      // idempotent on (readerId, bookId) — calling it when the book is
      // already bookmarked is a server-side no-op, so we don't need to
      // probe first. Fire-and-forget so a flaky bookmark request never
      // blocks the user from advancing into the progress screen.
      bookmarksService
        .create({ readerId, bookId: book.id })
        .catch((err) => console.warn('Auto-bookmark on plan create failed:', err));
      goTo('progress');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Could not save your reading plan.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDay = async (dayNumber: number) => {
    if (!goalFromStore) return;
    try {
      const updated = await readingTrackerService.markDay(goalFromStore.id, dayNumber);
      setGoal(key, updated);
      const msg = ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];
      toast.success(msg);
      if (updated.status === 'completed') {
        // 700ms wait so the dot fills and the bar animates before the
        // confetti rains down — same easing the route page used.
        window.setTimeout(() => {
          void launchConfetti();
        }, 700);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string; nextExpected?: number } | null;
        if (body?.error === 'OUT_OF_ORDER' && typeof body.nextExpected === 'number') {
          toast(`Complete Day ${body.nextExpected} first! 📅`, { duration: 3000 });
          return;
        }
      }
      toast.error(err instanceof ApiError ? err.message : 'Could not save that day.');
    }
  };

  const handleStartOver = async () => {
    if (!goalFromStore) return;
    try {
      await readingTrackerService.abandon(goalFromStore.id);
    } catch (err) {
      // Even if abandon fails (e.g. already terminal), reset locally so
      // the user gets the fresh start they asked for. The next mount
      // will reconcile against the server.
      console.warn('[reading-tracker] abandon failed:', err);
    }
    resetKey(key);
    setScreen(key, 'bookGoal');
    setGoal(key, null);
  };

  // ----- Render -----------------------------------------------------------
  const stepNumber = useMemo(() => screenToStep(screen), [screen]);

  // Effective counts for the wizard. Falls back to the catalogue counts off
  // the (refetched) book when the account-metadata fetch hasn't landed or
  // failed, so the picker always has something to work with.
  const effectiveMeta: BookAccountMetadataView = accountMeta ?? {
    bookId: book.id,
    pages: renderBook.pages,
    chapters: renderBook.chapters,
    override: { pages: null, chapters: null },
    global: { pages: renderBook.pages, chapters: renderBook.chapters },
  };

  if (bootstrapping) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <InlineLoader label="Loading your reading plan…" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onBackToBook}
        className="inline-flex w-fit items-center gap-1 text-[13px] font-bold text-or-orange-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40 focus-visible:ring-offset-2 rounded"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        Back to book
      </button>

      {/* Step indicator hides on the progress screen — by then the wizard
          is over and the indicator just adds noise. */}
      {screen !== 'progress' ? (
        <div className="flex justify-center">
          <StepIndicator current={stepNumber} total={2} />
        </div>
      ) : null}

      <motion.div
        key={screen}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {screen === 'bookGoal' && (
          <BookGoalScreen
            accountMeta={effectiveMeta}
            goalType={draft.goalType}
            perDay={draft.perDay}
            onGoalTypeChange={handleGoalTypeChange}
            onPerDayChange={handlePerDayChange}
            onSubmit={() => goTo('summary')}
            onBack={onBackToBook}
            onSaveCounts={handleSaveCounts}
          />
        )}
        {screen === 'summary' && draft.goalType && (
          <SummaryScreen
            book={renderBook}
            effectiveCounts={{ pages: effectiveMeta.pages, chapters: effectiveMeta.chapters }}
            goalType={draft.goalType}
            perDay={draft.perDay}
            onConfirm={handleConfirmPlan}
            onBack={() => goTo('bookGoal')}
            submitting={submitting}
          />
        )}
        {screen === 'progress' && goalFromStore && (
          <ProgressScreen
            goal={goalFromStore}
            onMarkDay={handleMarkDay}
            onStartOver={handleStartOver}
            onBackToBook={onBackToBook}
          />
        )}
        {screen === 'progress' && !goalFromStore && (
          <div className="rounded-2xl border border-or-slate-200 bg-white p-6 text-center text-[14px] text-or-slate-600">
            No active goal yet — let&apos;s set one up.
            <div className="mt-3">
              <button
                type="button"
                onClick={() => goTo('bookGoal')}
                className="text-[13px] font-bold text-or-orange-600 hover:underline"
              >
                Start the wizard
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/**
 * Step indicator math for the in-modal flow:
 *   bookGoal → 1, summary → 2 (no `book` recap step in this surface).
 *   `progress` returns 2 too but the indicator is hidden once we get
 *   there, so the value is unobservable.
 */
function screenToStep(screen: WizardScreen): number {
  switch (screen) {
    case 'book':
      return 1;
    case 'bookGoal':
      return 1;
    case 'summary':
      return 2;
    case 'progress':
      return 2;
  }
}
